import { apiService } from './api.js'

const HEARTBEAT_INTERVAL = 45000 // 45 seconds
let heartbeatInterval = null
let isChecking = false
const shownNotifications = new Set()

// Callbacks
let onNewDeposit = null
let onBalanceUpdate = null

/**
 * Start deposit heartbeat monitoring
 * @param {string} userId - User ID to monitor
 * @param {object} callbacks - { onNewDeposit, onBalanceUpdate }
 */
export function startDepositHeartbeat(userId, callbacks = {}) {
  if (heartbeatInterval) {
    console.log('[DEPOSIT_HEARTBEAT] Already running')
    return
  }

  if (!userId) {
    console.log('[DEPOSIT_HEARTBEAT] No userId provided')
    return
  }

  onNewDeposit = callbacks.onNewDeposit || null
  onBalanceUpdate = callbacks.onBalanceUpdate || null

  console.log('[DEPOSIT_HEARTBEAT] Starting heartbeat for user:', userId)

  // First check immediately
  checkDeposits(userId)

  // Then every 45 seconds
  heartbeatInterval = setInterval(() => {
    checkDeposits(userId)
  }, HEARTBEAT_INTERVAL)

  // Pause when tab is not visible
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      pauseHeartbeat()
    } else {
      resumeHeartbeat(userId)
    }
  })
}

/**
 * Stop deposit heartbeat
 */
export function stopDepositHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval)
    heartbeatInterval = null
    console.log('[DEPOSIT_HEARTBEAT] Stopped')
  }
}

/**
 * Pause heartbeat (tab hidden)
 */
function pauseHeartbeat() {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval)
    heartbeatInterval = null
    console.log('[DEPOSIT_HEARTBEAT] Paused (tab hidden)')
  }
}

/**
 * Resume heartbeat (tab visible)
 */
function resumeHeartbeat(userId) {
  if (!heartbeatInterval && userId) {
    console.log('[DEPOSIT_HEARTBEAT] Resuming')
    checkDeposits(userId)
    heartbeatInterval = setInterval(() => {
      checkDeposits(userId)
    }, HEARTBEAT_INTERVAL)
  }
}

/**
 * Check for new deposits
 */
async function checkDeposits(userId) {
  if (isChecking) return
  isChecking = true

  try {
    const response = await apiService.checkDeposits(userId)

    if (response.ok && response.data && response.data.new_deposits && response.data.new_deposits.length > 0) {
      console.log('[DEPOSIT_HEARTBEAT] New deposits found:', response.data.new_deposits.length)

      for (const deposit of response.data.new_deposits) {
        const notificationKey = `deposit_notif_${deposit.tx_hash || deposit.deposit_id}`

        // Show notification only once per deposit
        if (!sessionStorage.getItem(notificationKey) && !shownNotifications.has(notificationKey)) {
          shownNotifications.add(notificationKey)
          sessionStorage.setItem(notificationKey, 'true')

          if (onNewDeposit) {
            onNewDeposit(deposit)
          }
        }
      }

      // Trigger balance update
      if (onBalanceUpdate) {
        onBalanceUpdate(response.data.new_balance)
      }

      // Dispatch global event for other components
      window.dispatchEvent(new CustomEvent('balance-updated', {
        detail: { newBalance: response.data.new_balance }
      }))
    }
  } catch (error) {
    console.error('[DEPOSIT_HEARTBEAT] Error checking deposits:', error)
  } finally {
    isChecking = false
  }
}

export default {
  startDepositHeartbeat,
  stopDepositHeartbeat
}
